import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Skeleton,
  Tooltip,
  Typography
} from '@mui/material';
import {ArrowBackIos, ArrowForwardIos, AttachFile, Close, Email, Event, Notes} from '@mui/icons-material';
import {t} from 'ttag';
import {createDatetime, DATETIME_FORMAT_NO_SEC} from '../../../utils/datetime/datetime-utils';
import {IClaim, IClaimTimelineItem} from '../../../store/apis/interfaces/claim';

interface ClaimTimelineProps {
  data?: IClaim;
}

type TimelineItemType = 'document' | 'email' | 'note' | 'event';

function getItemType(item: IClaimTimelineItem): TimelineItemType {
  if (item.activityTypeCode === 'email') return 'email';
  if (item.fileName) return 'document';
  if (!item.activityTypeCode) return 'note';
  return 'event';
}

const TYPE_META: Record<TimelineItemType, {label: string; icon: React.ReactElement; color: 'default' | 'primary' | 'info' | 'warning'}> = {
  email:    {label: t`Email`,    icon: <Email fontSize='small' />,      color: 'primary'},
  document: {label: t`Document`, icon: <AttachFile fontSize='small' />, color: 'warning'},
  note:     {label: t`Note`,     icon: <Notes fontSize='small' />,      color: 'info'},
  event:    {label: t`Event`,    icon: <Event fontSize='small' />,      color: 'default'},
};

const COLOR_BG: Record<TimelineItemType, string> = {
  email:    'primary.main',
  document: 'warning.main',
  note:     'info.main',
  event:    'action.active',
};

const COLOR_RING: Record<TimelineItemType, string> = {
  email:    'primary.light',
  document: 'warning.light',
  note:     'info.light',
  event:    'grey.400',
};

// ─── TimelineConnector ────────────────────────────────────────────────────────

const TimelineConnector = () => (
  <Box sx={{flexGrow: 1, minWidth: 24, height: 2, bgcolor: 'divider', alignSelf: 'center'}} />
);

// ─── TimelineNode ─────────────────────────────────────────────────────────────

interface TimelineNodeProps {
  item: IClaimTimelineItem;
  isSelected: boolean;
  onClick: () => void;
}

const TimelineNode = ({item, isSelected, onClick}: TimelineNodeProps) => {
  const type = getItemType(item);
  const meta = TYPE_META[type];
  const label = item.subject || item.fileName || '—';
  const dt = createDatetime(item.createdOn);
  const datePart = dt.toFormat('dd/MM/yyyy');
  const timePart = dt.toFormat('HH:mm');

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 80}}>
      {/* Label above */}
      <Tooltip title={label} placement='top'>
        <Typography
          variant='caption'
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            maxWidth: 72,
            mb: 0.5,
            textAlign: 'center',
            fontSize: '0.65rem',
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>
      </Tooltip>

      {/* Circle button */}
      <IconButton
        onClick={onClick}
        size='small'
        sx={{
          width: isSelected ? 48 : 40,
          height: isSelected ? 48 : 40,
          bgcolor: COLOR_BG[type],
          color: 'common.white',
          transition: 'all 0.2s ease',
          boxShadow: isSelected
            ? (theme) => `0 0 0 3px ${(theme.palette as any)[meta.color]?.light ?? theme.palette.grey[400]}, 0 2px 8px rgba(0,0,0,0.25)`
            : undefined,
          '&:hover': {
            bgcolor: COLOR_BG[type],
            filter: 'brightness(0.88)',
          },
        }}
      >
        {meta.icon}
      </IconButton>

      {/* Date below */}
      <Typography variant='caption' sx={{mt: 0.5, textAlign: 'center', fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.3}}>
        {datePart}<br />{timePart}
      </Typography>
    </Box>
  );
};

// ─── TimelineDetail ───────────────────────────────────────────────────────────

interface TimelineDetailProps {
  item: IClaimTimelineItem;
  onClose: () => void;
}

const TimelineDetail = ({item, onClose}: TimelineDetailProps) => {
  const type = getItemType(item);
  const meta = TYPE_META[type];
  const label = item.subject || item.fileName || '—';
  const dateStr = createDatetime(item.createdOn).toFormat(DATETIME_FORMAT_NO_SEC);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mt: 1,
        p: 2,
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box display='flex' alignItems='center' gap={1} mb={1} pr={4} flexWrap='wrap'>
        <Chip size='small' label={meta.label} color={meta.color} icon={meta.icon} sx={{fontFamily: 'OpenSansMedium'}} />
        <Typography
          variant='body2'
          fontFamily='OpenSansMedium'
          sx={{flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
        >
          {label}
        </Typography>
        <Typography variant='caption' color='text.secondary' whiteSpace='nowrap'>{dateStr}</Typography>
      </Box>

      {/* Close button */}
      <IconButton size='small' onClick={onClose} sx={{position: 'absolute', top: 8, right: 8}}>
        <Close fontSize='small' />
      </IconButton>

      <Divider sx={{mb: 1}} />

      {/* Body */}
      {type === 'event' && (
        <Typography variant='body2' color='text.secondary'>
          {item.subject || t`No additional details.`}
        </Typography>
      )}
      {type === 'email' && (
        <Box display='flex' flexDirection='column' gap={0.5}>
          <Typography variant='body2'>{item.subject}</Typography>
          {item.from && (
            <Typography variant='body2'>
              <Box component='span' fontFamily='OpenSansMedium'>{t`From`}:</Box> {item.from}
            </Typography>
          )}
          {item.to && (
            <Typography variant='body2'>
              <Box component='span' fontFamily='OpenSansMedium'>{t`To`}:</Box> {item.to.replace(/;+$/, '')}
            </Typography>
          )}
        </Box>
      )}
      {type === 'note' && (
        <Typography variant='body2'>{item.subject}</Typography>
      )}
      {type === 'document' && (
        <Box display='flex' alignItems='center' gap={1}>
          <AttachFile fontSize='small' color='action' />
          <Typography variant='body2'>{item.fileName || t`Document`}</Typography>
        </Box>
      )}
    </Box>
  );
};

// ─── Skeleton node ────────────────────────────────────────────────────────────

const SkeletonNode = ({isLast}: {isLast: boolean}) => (
  <>
    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 80}}>
      <Skeleton variant='text' width={60} sx={{mb: 0.5}} />
      <Skeleton variant='circular' width={40} height={40} />
      <Skeleton variant='text' width={50} sx={{mt: 0.5}} />
    </Box>
    {!isLast && (
      <Box sx={{flexGrow: 1, minWidth: 24, height: 2, bgcolor: 'action.disabledBackground', alignSelf: 'center'}} />
    )}
  </>
);

// ─── ClaimTimeline ────────────────────────────────────────────────────────────

const SCROLL_STEP = 240;

export const ClaimTimeline = ({data}: ClaimTimelineProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  // newest first (descending)
  const sorted = data?.Timeline
    ? [...data.Timeline].sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
    : undefined;

  const selectedItem = sorted?.find((item) => item.id === selectedId) ?? null;

  const handleNodeClick = (id: string) => setSelectedId((prev) => (prev === id ? null : id));

  const scrollToDetail = () => detailRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});

  useEffect(() => {
    if (!selectedId) return;
    // wait for Collapse animation (~300ms) before scrolling
    const timer = setTimeout(scrollToDetail, 320);
    return () => clearTimeout(timer);
  }, [selectedId]);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener('scroll', updateScrollButtons);
    const ro = new ResizeObserver(updateScrollButtons);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', updateScrollButtons); ro.disconnect(); };
  }, [sorted, updateScrollButtons]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({left: direction === 'left' ? -SCROLL_STEP : SCROLL_STEP, behavior: 'smooth'});
  };

  return (
    <Card elevation={3}>
      <CardHeader title={<Typography sx={{fontFamily: 'OpenSansMedium'}}>{t`Timeline`}</Typography>} />
      <Divider />
      <CardContent>
        {/* Loading skeleton */}
        {!data && (
          <Box sx={{display: 'flex', alignItems: 'center', overflowX: 'auto', py: 4, px: 1}}>
            {[1, 2, 3].map((i) => <SkeletonNode key={i} isLast={i === 3} />)}
          </Box>
        )}

        {/* Empty state */}
        {data && sorted?.length === 0 && (
          <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 128}}>
            <Typography variant='body2'>{t`No timeline events found.`}</Typography>
          </Box>
        )}

        {/* Timeline row */}
        {data && sorted && sorted.length > 0 && (
          <>
            <Box sx={{position: 'relative'}}>
              {/* Left scroll button */}
              {canScrollLeft && (
                <IconButton
                  size='small'
                  onClick={() => scroll('left')}
                  sx={{
                    position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)',
                    zIndex: 1, bgcolor: 'background.paper', boxShadow: 2,
                    '&:hover': {bgcolor: 'background.paper'},
                  }}
                >
                  <ArrowBackIos fontSize='small' />
                </IconButton>
              )}

              {/* Right scroll button */}
              {canScrollRight && (
                <IconButton
                  size='small'
                  onClick={() => scroll('right')}
                  sx={{
                    position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
                    zIndex: 1, bgcolor: 'background.paper', boxShadow: 2,
                    '&:hover': {bgcolor: 'background.paper'},
                  }}
                >
                  <ArrowForwardIos fontSize='small' />
                </IconButton>
              )}

              <Box ref={scrollRef} sx={{display: 'flex', alignItems: 'center', overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': {display: 'none'}, py: 4, px: canScrollLeft || canScrollRight ? 4 : 1}}>
              {sorted.map((item, index) => (
                <React.Fragment key={item.id}>
                  <TimelineNode
                    item={item}
                    isSelected={selectedId === item.id}
                    onClick={() => handleNodeClick(item.id)}
                  />
                  {index < sorted.length - 1 && <TimelineConnector />}
                </React.Fragment>
              ))}
              </Box>
            </Box>

            {/* Detail panel */}
            <Collapse in={selectedItem !== null} unmountOnExit>
              <div ref={detailRef}>
                {selectedItem && <TimelineDetail item={selectedItem} onClose={() => setSelectedId(null)} />}
              </div>
            </Collapse>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ClaimTimeline;
